<template>
  <div class="tracker-panel">

    <div class="grid grid-4">
      <div class="metric-card">
        <div class="metric-card-label">Long Units P&L</div>
        <div class="metric-card-value" :class="totals.pnl >= 0 ? 'profit-positive' : 'profit-negative'">
          {{ totals.pnl.toFixed(2) }} USD
        </div>
        <div class="metric-card-secondary" :class="totals.pnlPct >= 0 ? 'profit-positive' : 'profit-negative'">{{ totals.pnlPct.toFixed(2) }}%</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">Qty Below Market</div>
        <div class="metric-card-value profit-positive">{{ totals.qtyBelow.toFixed(6) }} BTC</div>
        <div class="metric-card-secondary">Below Hedge: {{ totals.qtyBelowHedge.toFixed(6) }} BTC</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">P&L Positivo Total</div>
        <div class="metric-card-value profit-positive">{{ totals.pnlPositive.toFixed(2) }} USD</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">Posição Long Total</div>
        <div class="metric-card-value">{{ totals.qty.toFixed(6) }} BTC</div>
        <div class="metric-card-secondary">Avg: {{ totals.avgPrice.toFixed(2) }} | Notional: {{  totals.notional.toFixed(2) }}</div>
      </div>
    </div>

    <div class="units-section">
      <div class="units-label">Long Units</div>
      <div class="units-list grid grid-3 scrollable" v-if="manualUnits.length">
        <div v-for="unit in manualUnits" :key="unit.unitID" class="unit-chip" :class="{ 'profit-chip': pnl(unit) >= 0, 'loss-chip': pnl(unit) < 0 }">
          <div class="unit-badge">{{ unit.metadata?.excgAccount || 'N/A' }}</div>
          <div class="unit-id">{{ unit.unitID }}</div>
          <div class="unit-sub">Price: {{ Number(unit.current?.avgPrice || 0).toFixed(2) }} | Qty: {{ Number(unit.current?.qty || 0).toFixed(6) }}</div>
          <div class="unit-sub unit-target">Target: {{ targetPrice(unit).toFixed(2) }} (+{{ Number(targetMarginPct).toFixed(1) }}%)</div>
          <div class="unit-sub" :class="pnl(unit) >= 0 ? 'profit-positive' : 'profit-negative'">
            P&L: {{ pnl(unit).toFixed(2) }} ({{ pnlPct(unit) }}%)
          </div>
        </div>
      </div>
      <div v-else class="empty-msg">Nenhuma manual unit no ledger.</div>
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { calcSpreadPercent } from '../utils/funcs';

export default {
  name: 'unitsTracker',
  computed: {
    ...mapState({
      ledger:          state => state.ledger,
      shortLedger:     state => state.shortLedger,
      btcPrice:        state => state.marketData.btcUsd,
      targetMarginPct: state => state.targetMarginPct,
    }),
    ...mapGetters(['targetPctFrac']),

    manualUnits() {
      return Object.values(this.ledger || {})
        .filter(u => u.bucket === 'manual')
        .sort((a, b) => (a.current?.avgPrice || 0) - (b.current?.avgPrice || 0));
    },

    totals() {
      const qty = this.manualUnits.reduce((s, u) => s + Number(u.current?.qty || 0), 0);
      const totalValue = this.manualUnits.reduce((s, u) => s + Number(u.current?.avgPrice || 0) * Number(u.current?.qty || 0), 0);
      const avgPrice = qty > 0 ? totalValue / qty : 0;
      const qtyBelow = this.manualUnits.filter(u => Number(u.current?.avgPrice || 0) < this.btcPrice).reduce((s, u) => s + Number(u.current?.qty || 0), 0);
      const pnlPositive = this.manualUnits.reduce((s, u) => { const p = this.pnl(u); return p > 0 ? s + p : s; }, 0);
      const activeShorts = Object.values(this.shortLedger || {}).filter(u => u.status === 'active');
      const shortRemainingQty = activeShorts.reduce((s, u) => s + Number(u.remainingQty || 0), 0);
      const hedgeAvgSell = shortRemainingQty > 0
        ? activeShorts.reduce((s, u) => s + Number(u.origin?.sellPrice || 0) * Number(u.remainingQty || 0), 0) / shortRemainingQty
        : 0;
      const qtyBelowHedge = this.manualUnits.filter(u => Number(u.current?.avgPrice || 0) < hedgeAvgSell).reduce((s, u) => s + Number(u.current?.qty || 0), 0);
      return {
        qty,
        avgPrice: Math.round(avgPrice * 100) / 100,
        notional: Math.round(totalValue * 100) / 100,
        pnl: Math.round((this.btcPrice - avgPrice) * qty * 100) / 100,
        pnlPct: totalValue > 0 ? Math.round((this.btcPrice - avgPrice) / avgPrice * 10000) / 100 : 0,
        qtyBelow: Math.round(qtyBelow * 1e6) / 1e6,
        qtyBelowHedge: Math.round(qtyBelowHedge * 1e6) / 1e6,
        pnlPositive: Math.round(pnlPositive * 100) / 100,
      };
    },
  },

  methods: {
    pnl(unit) {
      const qty = Number(unit.current?.qty || 0);
      const avg = Number(unit.current?.avgPrice || 0);
      return Math.round((this.btcPrice - avg) * qty * 100) / 100;
    },
    pnlPct(unit) {
      const avg = Number(unit.current?.avgPrice || 0);
      return avg > 0 ? calcSpreadPercent(avg, this.btcPrice) : '0.00';
    },
    targetPrice(unit) {
      const avg = Number(unit.current?.avgPrice || 0);
      return avg * (1 + this.targetPctFrac);
    },
  },
};
</script>

<style scoped>

.units-list {
    height: 400px !important;
}

.unit-target {
  color: #e3b341;
  opacity: 1;
}

</style>
